import React from "react";
import {
  Card,
  MiniatureCanvas,
  MiniatureViewport,
  PreviewArea,
  RailActions,
  RailButton,
  RailFrame,
  RailItem,
  RailName,
  RailNumber,
  SLIDE_THUMBNAIL_PREVIEW_HEIGHT_PX,
  SLIDE_THUMBNAIL_PREVIEW_WIDTH_PX,
} from "./SlideThumbnail.styles";
import { SLIDE_PREVIEW_COMPONENTS } from "./SlideThumbnail.previews";
import type { SlideThumbnailProps } from "./SlideThumbnail.types";

/**
 * A single slide card in the present-mode filmstrip (Figma 3730:514953).
 * Stateless — the active slide index and click handling live in the parent
 * strip/organism.
 *
 * When `children` (the real detail-slide component) is provided, it is
 * rendered at its natural `slideWidth × slideHeight` and shrunk with
 * `transform: scale()` — the thumbnail is an exact miniature of the detail
 * view. Without children, the `preview` skeleton graphic is shown instead.
 */
export const SlideThumbnail: React.FC<SlideThumbnailProps> = ({
  title,
  preview,
  children,
  slideWidth = 1280,
  slideHeight = 720,
  isActive = false,
  onClick,
  variant = "card",
  number,
  actions,
  className,
}) => {
  const Preview = preview ? SLIDE_PREVIEW_COMPONENTS[preview] : null;
  const scale = Math.min(
    SLIDE_THUMBNAIL_PREVIEW_WIDTH_PX / slideWidth,
    SLIDE_THUMBNAIL_PREVIEW_HEIGHT_PX / slideHeight,
  );

  if (variant === "rail") {
    return (
      <RailItem $isActive={isActive} className={className}>
        {number != null ? <RailNumber aria-hidden="true">{number}</RailNumber> : null}
        <RailButton
          type="button"
          aria-pressed={isActive}
          aria-label={`Go to slide: ${title}`}
          title={title}
          onClick={onClick}
        >
          <RailFrame aria-hidden="true" data-testid="slide-rail-frame">
            {children ?? (Preview ? <Preview /> : null)}
          </RailFrame>
          <RailName>{title}</RailName>
        </RailButton>
        {actions ? <RailActions>{actions}</RailActions> : null}
      </RailItem>
    );
  }

  return (
    <Card
      type="button"
      $isActive={isActive}
      aria-pressed={isActive}
      aria-label={`Go to slide: ${title}`}
      title={title}
      onClick={onClick}
      className={className}
    >
      {children != null ? (
        <MiniatureViewport aria-hidden data-testid="slide-miniature">
          <MiniatureCanvas
            $width={slideWidth}
            $height={slideHeight}
            $scale={scale}
          >
            {children}
          </MiniatureCanvas>
        </MiniatureViewport>
      ) : (
        <PreviewArea>{Preview ? <Preview /> : null}</PreviewArea>
      )}
    </Card>
  );
};

export default SlideThumbnail;
